<template>
    <outside-click-detector @outside="hideList">
        <div class="select-input"
             :class="{
            'has-value': hasValue,
            'is-open': isListVisible,
        }"
        >
            <label class="select-input__label">
                {{ label }}
            </label>
            <input
                class="select-input__field"
                type="text"
                readonly
                :name="name"
                :value="htmlEntityDecode(value)"
                :disabled="disabled"
                @change="onChange"
                @click="toggleListVisibility"
                @keydown.up.prevent="selectPrevItem"
                @keydown.down.prevent="selectNextItem"
                @keydown.enter.prevent="hideList"
                @keydown.esc.prevent="hideList"
            >
            <button
                class="select-input__clear"
                type="button"
                tabindex="-1"
                v-if="clearable && hasValue && !disabled"
                @click="clear"
            >
                <svg><use xlink:href="/sprite.svg#icon-close"></use></svg>
            </button>

            <svg class="select-input__arrow">
                <use xlink:href="/sprite.svg#icon-chevron-collapse"></use>
            </svg>

            <div
                class="select-input__status"
                v-if="statusText"
            >
                {{ statusText }}
            </div>

            <template v-if="!isComplex">
                <div class="select-input__option-list-wrap" v-if="isListVisible && items.length">
                    <ul class="select-input__option-list">
                        <li class="select-input__option"
                            v-for="(item, index) in sortedItems"
                            :key="index"
                            :class="{ 'selected': currentItemIndex === index }"
                            @click="selectItem(itemValueKey ? item[itemValueKey] : item)"
                        >
                            {{ itemValueKey ? item[itemValueKey] : item }}
                        </li>
                    </ul>
                </div>
            </template>

            <template v-if="isComplex && component">
                <component
                    :is="component"
                    :items="items"
                    :sort-key="sortKey"
                    :isListVisible="isListVisible"
                    :currentItemIndex="currentItemIndex"
                    @click="selectComplexItem"
                />
            </template>
        </div>
    </outside-click-detector>
</template>

<script>
import OutsideClickDetector from '../../dom-helpers/outside-click-detector';

export default {
    name: 'select-input',
    components: { OutsideClickDetector },
    model: {
        prop: 'selectedItem',
        event: 'change',
    },

    props: {
        name: {
            required: true,
            type: String,
        },
        label: {
            required: true,
            type: String,
        },
        items: {
            default: [],
        },
        itemValueKey: {
            required: false,
            type: String,
        },
        selectedItem: {
            required: false,
            type: String,
        },
        status: {
            required: false,
            type: String,
        },
        disabled: {
            required: false,
            type: Boolean,
        },
        clearable: {
            default: false,
        },
        hint: {
            required: false,
            type: String,
        },

        // для запроса с параметрами
        isComplex: {
            required: false,
            type: Boolean,
            default: false,
        },

        component: {
            required: false,
            type: String,
        },

        sortKey: {
            type: String,
            default: 'VALUE',
        },
    },

    data() {
        return {
            hasFocus: false,
            value: this.htmlEntityDecode(this.selectedItem),
            isListVisible: false,
        };
    },

    computed: {
        hasValue() {
            return !!this.value;
        },
        statusText() {
            return this.status || this.hint;
        },
        currentItemIndex() {
            return this.sortedItems.indexOf(this.value);
        },
        isFirstItem() {
            return this.currentItemIndex === 0;
        },
        isLastItem() {
            return this.currentItemIndex === this.sortedItems.length - 1;
        },
        sortedItems() {
            const { items } = this;
            const key = this.itemValueKey;
            return key ? items.sort((a, b) => a[key].localeCompare(b[key])) : items.sort();
        },

        // isItemsArray и isItemsObject появились в последствии
        // они нужны, чтобы разделять типы выпадашек
        // первый: просто для списка со значениями (для меня, логика происходящего чуть перегружена)
        // второй: более сложный, с параметрами
        isItemsArray() {
            return Array.isArray(this.items);
        },
    },

    methods: {
        onChange(e) {
            this.$emit('change', e.target.value);
        },

        createContactRow(item) {
            const name = item.NAME ? item.NAME : '';
            const phone = item.PERSONAL_PHONE ? `, ${this.formatPhone(item.PERSONAL_PHONE)}` : '';
            return name + phone;
        },

        selectItem(item) {
            this.value = item;
            this.hideList();
            this.$emit('change', this.value);
        },

        /**
         * Логика изначально была расчитана на использование только для имени и номера телефона
         * Затем расширилась
         * @param evt
         * @param item
         */
        selectComplexItem(evt, item) {
            const contacts = this.createContactRow(item);
            this.value = contacts || item.VALUE; // или сторку с именем (и телефоном по возможности), или просто VALUE

            this.hideList();
            this.$emit('get-item', item, this.label);
        },

        selectPrevItem() {
            if (this.isComplex) return;

            if (this.isListVisible && !this.isFirstItem) {
                const prevItem = this.sortedItems[this.currentItemIndex - 1];
                this.value = this.itemValueKey ? prevItem[this.itemValueKey] : prevItem;
                this.$emit('change', this.value);
            }
        },

        selectNextItem() {
            if (this.isComplex) return;

            if (this.isListVisible && !this.isLastItem) {
                const nextItem = this.sortedItems[this.currentItemIndex + 1];
                this.value = this.itemValueKey ? nextItem[this.itemValueKey] : nextItem;
                this.$emit('change', this.value);
            }
        },

        showList() {
            this.isListVisible = true;
        },

        hideList() {
            this.isListVisible = false;
        },

        toggleListVisibility() {
            if (this.isListVisible) {
                this.hideList();
            } else {
                this.showList();
            }
        },

        clear() {
            this.$emit('change', '');
        },

        htmlEntityDecode(value) {
            if (!value || typeof value !== 'string') return '';
            const txt = document.createElement('textarea');
            txt.innerHTML = value.replaceAll('&amp;', '&');
            return txt.value;
        },

        formatPhone(phone) {
            const arr = `${phone}`.split('');

            return arr.reduce((prev, curr, index) => {
                switch (index) {
                case 0:
                    return '+7 (';
                case 4:
                    return `${prev}) ${curr}`;
                case 7:
                    return `${prev}-${curr}`;
                case 9:
                    return `${prev}-${curr}`;
                default:
                    return `${prev}${curr}`;
                }
            }, '');
        },
    },

    watch: {
        /**
         * Иначе, при переоткрытии с другими параметрами используется предыдущее значение в качестве выбранного
         * @param value
         */
        selectedItem(value) {
            if (this.isComplex) {
                this.value = this.htmlEntityDecode(value);
            }
        },
    },
};
</script>
